import { Button, Modal } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import { EntityType } from '../../../../../../types.generated';
import ClickOutside from '../../../../../shared/ClickOutside';
import { EntityAndType } from '../../../types';
import { SearchSelect } from './SearchSelect';
import { useTranslation } from '../../../../../../i18n/useTranslation';

const StyledModal = styled(Modal)`
    top: 30px;

    .ant-modal-body {
        @media (max-width: 992px) {
            height: auto !important;
        }

        .f-default-preview-card {
            overflow: hidden;

            .f-left-column {
                overflow: hidden;

                .f-layout-wrap {
                    flex-wrap: wrap;
                }

                .f-description-container {
                    width: auto;
                }
            }

            .f-right-column {
                flex-direction: column;
            }
        }
    }
`;

const MODAL_WIDTH_PX = 800;

const MODAL_BODY_STYLE = { padding: 0, height: '70vh' };

type Props = {
    fixedEntityTypes?: Array<EntityType> | null;
    placeholderText?: string | null;
    titleText?: string | null;
    continueText?: string | null;
    onContinue: (entityUrns: string[]) => void;
    onCancel?: () => void;
    showLessPaginationItems?: boolean;
};

/**
 * Modal that can be used for searching & selecting a subset of the entities in the Metadata Graph in order to take a specific action.
 *
 * This component provides easy ways to filter for a specific set of entity types, and provides a set of entity urns
 * when the selection is complete.
 */
export const SearchSelectModal = ({
    fixedEntityTypes,
    placeholderText,
    titleText,
    continueText,
    onContinue,
    onCancel,
    showLessPaginationItems,
}: Props) => {
    const { t } = useTranslation();
    const [selectedEntities, setSelectedEntities] = useState<EntityAndType[]>([]);

    const onCancelSelect = () => {
        if (selectedEntities.length > 0) {
            Modal.confirm({
                centered: true,
                title: t('Exit Selection'),
                content: t('Are you sure you want to exit? {{count}} selection(s) will be cleared.', {
                    count: selectedEntities.length,
                }),
                onOk() {
                    onCancel?.();
                },
                onCancel() {},
                okText: t('Yes'),
                cancelText: t('Cancel'),
                maskClosable: true,
                closable: true,
            });
        } else {
            onCancel?.();
        }
    };

    return (
        <ClickOutside onClickOutside={onCancelSelect} wrapperClassName="search-select-modal">
            <StyledModal
                wrapClassName="search-select-modal"
                bodyStyle={MODAL_BODY_STYLE}
                title={titleText || t('Select entities')}
                width={MODAL_WIDTH_PX}
                visible
                className="f-modal"
                centered
                onCancel={onCancelSelect}
                footer={
                    <>
                        <Button onClick={onCancel} type="default">
                            {t('Cancel')}
                        </Button>
                        <Button
                            type="primary"
                            id="continueButton"
                            disabled={selectedEntities.length === 0}
                            onClick={() => onContinue(selectedEntities.map((entity) => entity.urn))}
                        >
                            {continueText || t('Done')}
                        </Button>
                    </>
                }
            >
                <SearchSelect
                    fixedEntityTypes={fixedEntityTypes}
                    placeholderText={placeholderText}
                    selectedEntities={selectedEntities}
                    setSelectedEntities={setSelectedEntities}
                    showLessPaginationItems={showLessPaginationItems}
                />
            </StyledModal>
        </ClickOutside>
    );
};
